import { useEffect, useMemo, useState } from "react";
import {
  listenToBoardCards,
  listenToBoardStages,
} from "../../../../database/board/boardApi";
import {
  BoardCard,
  BoardStageDef,
  DEFAULT_BOARD_STAGES,
  PRIORITY_ORDER,
} from "../../../../types/Board";

// Live view of the global issue board: the editable stage config plus all
// cards grouped per stage. Cards inside a column keep their sparse-float
// position order (the drag order), with priority as a tiebreak.

export type BoardState = {
  stages: BoardStageDef[];
  cards: BoardCard[];
  byStage: Record<string, BoardCard[]>;
  loading: boolean;
};

export const useBoard = (): BoardState => {
  const [stages, setStages] = useState<BoardStageDef[]>(DEFAULT_BOARD_STAGES);
  const [cards, setCards] = useState<BoardCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubStages = listenToBoardStages(setStages);
    const unsubCards = listenToBoardCards(
      (next) => {
        setCards(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => {
      unsubStages();
      unsubCards();
    };
  }, []);

  return useMemo(() => {
    const byStage: Record<string, BoardCard[]> = {};
    stages.forEach((s) => (byStage[s.key] = []));
    cards.forEach((c) => (byStage[c.status] || (byStage[c.status] = [])).push(c));
    Object.values(byStage).forEach((list) =>
      list.sort(
        (a, b) =>
          a.position - b.position ||
          PRIORITY_ORDER[b.priority] - PRIORITY_ORDER[a.priority]
      )
    );
    return { stages, cards, byStage, loading };
  }, [stages, cards, loading]);
};

export default useBoard;
